/**
 * The event banners' queue (GDD §7.3; concept announce L951–L954, newGame L1143–L1144, queue L1197–L1199). It steps
 * in simulation time, so a paused game freezes its banners. A banner shows for 2.2 s (2.8 s for JET LOST, 3.6 s for
 * GAME OVER), with a 0.35 s gap before the next so each one animates in. A lost jet or GAME OVER interrupts: the
 * banner showing and everything queued are dropped. GAME OVER shows at once, so it lasts exactly as long as the
 * game-over pause before the title. A new run clears the queue and waits 1.2 s. Nothing is allocated.
 */
import { config } from '../core/config.ts';
import type { CrashCause } from '../sim/state.ts';

/** What a banner announces. */
export type BannerKey = 'level' | 'bridge' | 'extraJet' | 'jetLost' | 'gameOver';
/** §7.3 style: cyan, amber or red. */
export type BannerKind = 'level' | 'good' | 'danger';

export interface Banner {
  key: BannerKey;
  kind: BannerKind;
  /** Level for 'level' banners. */
  level: number;
  cause: CrashCause | null;
  /** Seconds it shows. */
  dur: number;
}

export interface Announcer {
  /** The banner showing; valid while `showing`. */
  readonly current: Banner;
  showing: boolean;
  /** Increases with every banner shown, so the HUD knows to animate a new one in. */
  id: number;
  /** Seconds left on the banner showing (concept msgT). */
  left: number;
  /** Seconds before the next queued banner may show (concept gap). */
  gap: number;
}

/** The pause between banners, and before a new run's first one (concept L952, L1143). */
const GAP = config.banners.gapSec;
const NEW_RUN_GAP = 1.2;
const QUEUE = 8;

interface State extends Announcer {
  queue: Banner[];
  head: number;
  count: number;
}

const blank = (): Banner => ({ key: 'level', kind: 'level', level: 0, cause: null, dur: 0 });

export function createAnnouncer(): Announcer {
  const s: State = {
    current: blank(),
    showing: false,
    id: 0,
    left: 0,
    gap: 0,
    queue: Array.from({ length: QUEUE }, blank),
    head: 0,
    count: 0,
  };
  return s;
}

export function bannerKind(key: BannerKey): BannerKind {
  return key === 'level' ? 'level' : key === 'jetLost' || key === 'gameOver' ? 'danger' : 'good';
}

export function bannerSeconds(key: BannerKey): number {
  const b = config.banners;
  return key === 'gameOver' ? b.gameOverSec : key === 'jetLost' ? b.dangerSec : b.normalSec;
}

function fill(b: Banner, key: BannerKey, level: number, cause: CrashCause | null): void {
  b.key = key;
  b.kind = bannerKind(key);
  b.level = level;
  b.cause = cause;
  b.dur = bannerSeconds(key);
}

/** Queues a banner; a lost jet or GAME OVER interrupts instead (concept announce). */
export function announce(a: Announcer, key: BannerKey, level = 0, cause: CrashCause | null = null): void {
  const s = a as State;
  if (bannerKind(key) === 'danger') {
    s.count = 0;
    s.showing = false;
    s.left = 0;
    // GAME OVER shows at once, so its 3.6 s end as the title returns (GDD §4.6, §7.1).
    s.gap = key === 'gameOver' ? 0 : GAP;
  }
  if (s.count >= QUEUE) return; // more than a screenful queued: drop the newest
  const b = s.queue[(s.head + s.count) % QUEUE] as Banner;
  fill(b, key, level, cause);
  s.count++;
}

/** A new run: nothing queued, nothing showing, 1.2 s before its first banner (concept newGame). */
export function resetAnnouncer(a: Announcer): void {
  const s = a as State;
  s.count = 0;
  s.showing = false;
  s.left = 0;
  s.gap = NEW_RUN_GAP;
}

/** One step (concept L1197–L1199). */
export function stepAnnouncer(a: Announcer, dt: number): void {
  const s = a as State;
  if (s.left > 0) {
    s.left -= dt;
    if (s.left <= 0) {
      s.showing = false;
      s.gap = GAP;
    }
  } else if (s.gap > 0) s.gap -= dt;
  else if (s.count > 0) {
    const b = s.queue[s.head] as Banner;
    s.head = (s.head + 1) % QUEUE;
    s.count--;
    const c = s.current;
    c.key = b.key;
    c.kind = b.kind;
    c.level = b.level;
    c.cause = b.cause;
    c.dur = b.dur;
    s.left = b.dur;
    s.showing = true;
    s.id++;
  }
}
